import Vue from 'vue';
import {
    getPlaylists,
    getPlaylistById,
    updatePlaylist,
    bulkModifyPlaylists,
    createPlaylist,
    deletePlaylist,
} from './playlists.utils';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        updating: false,
        playlists: {},
        currentPlaylistId: null,
    },
    mutations: {
        setMap (state, { mapName, value }) {
            state[mapName] = value;
        },
        setMapAtKey (state, { mapName, key, value }) {
            Vue.set(state[mapName], key, value);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setUpdating (state, { updating }) {
            state.updating = updating;
        },
        setCurrentPlaylistId (state, id) {
            state.currentPlaylistId = id;
        },
        deleteMapAtKey (state, { mapName, key }) {
            Vue.delete(state[mapName], key);
        },
    },
    actions: {
        async loadAll ({ commit, state }, { reFetch = false, createdByMe = true } = {}) {
            if (!isEmpty(state.playlists) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            commit('setError', { error: null });

            try {
                const playlists = await getPlaylists(createdByMe);
                commit('setFetching', { fetching: false });

                commit('setMap', {
                    mapName: 'playlists',
                    value: keyBy(playlists, 'id'),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async loadOne ({ commit, getters }, { reFetch = false, id, abortSignal }) {
            if (!reFetch && getters.playlistById(id)) {
                commit('setCurrentPlaylistId', id);

                return;
            }

            commit('setFetching', { fetching: true });
            commit('setError', { error: null });

            try {
                const playlist = await getPlaylistById(id, abortSignal);

                commit('setCurrentPlaylistId', playlist.id);
                commit('setFetching', { fetching: false });

                commit('setMapAtKey', {
                    mapName: 'playlists',
                    key: id,
                    value: playlist,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async create ({ commit, rootGetters }, { playlist }) {
            commit('setFetching', { fetching: true });

            return createPlaylist(playlist, rootGetters['subscription/getActiveSub'])
                .then((res) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        mapName: 'playlists',
                        key: res.id,
                        value: res,
                    });

                    return res;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async update ({ commit, dispatch }, { playlist }) {
            commit('setUpdating', { updating: true });
            commit('setError', { error: null });

            try {
                const updatedPlaylist = await updatePlaylist(playlist);
                await dispatch('loadOne', { id: updatedPlaylist.id, reFetch: true });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setUpdating', { updating: false });
            }
        },
        async bulkModifyPlaylists ({ commit }, { modifyPlaylistsBody }) {
            commit('setUpdating', { updating: true });
            commit('setError', { error: null });

            try {
                const updatedPlaylists = await bulkModifyPlaylists(modifyPlaylistsBody);

                updatedPlaylists.forEach((playlist) => {
                    commit('setMapAtKey', {
                        mapName: 'playlists',
                        key: playlist.id,
                        value: playlist,
                    });
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setUpdating', { updating: false });
            }
        },
        deleteOne ({ commit }, { playlistId }) {
            commit('setFetching', { fetching: true });
            commit('setError', { error: null });

            return deletePlaylist(playlistId)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapAtKey', { mapName: 'playlists', key: playlistId });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
    },
    getters: {
        listAll (state) {
            return values(state.playlists);
        },
        playlistById: (state) => (id) => {
            return state.playlists[id];
        },
        currentPlaylist: (state) => state.playlists[state.currentPlaylistId],
    },
};
